/**
 * P13-PROD-SCALE-BOUNDED-READS-WS1 · R0-SCL-01 — the agency (Point) catalogue, read page by page.
 *
 * `GET /api/points` and `GET /api/agencies` (one backend handler) no longer return the whole estate: they
 * answer ONE bounded page (default 50, at most 200 rows) with
 * `{ success, data, total, pagination: { page, limit, total, pages, hasMore } }`, where `total` is the size
 * of the whole scoped set. Every consumer below needs the COMPLETE catalogue: pickers, the map, the
 * agency table, find-by-id lookups. So each one reads it through this walker, and never treats a first
 * page as the catalogue.
 *
 * ALL OR NOTHING. The walker returns the complete set or throws `PointCatalogueIncompleteError`. It never
 * returns a prefix. It throws on:
 *   · a page without the pagination contract;
 *   · an id repeated across pages;
 *   · an empty page while `hasMore` is true;
 *   · a collected count that differs from the server's `total` (a write in the set while it was read);
 *   · an exhausted page budget.
 * Each consumer keeps its existing error path for the throw, so a failed catalogue read is shown as a
 * failure (or an empty catalogue where that was already the behaviour), never as a shorter list.
 *
 * SHARED. `readAllCataloguePages` is the one walker of every bounded catalogue: the agency catalogue here and
 * the group catalogue (`groupCatalogue.ts`, P13-PROD-SCALE-ORG-REPORTING-BOUNDED-READS-WS1) read through it,
 * each with its own error type and wording.
 *
 * TRANSPORT. The walker does not own HTTP. Each consumer passes a `fetchPage(query)` that returns the
 * page ENVELOPE with its own client. The canonical shared client unwraps `{ success, data }` by default
 * and would drop `pagination`, so shared-client consumers must request `skipUnwrap: true`
 * (`sharedClientPointPages` below does that).
 */
export const POINT_CATALOGUE_PAGE_LIMIT = 200
export const POINT_CATALOGUE_MAX_PAGES = 10000

export interface PointPagination {
  page: number
  limit: number
  total: number
  pages: number
  hasMore: boolean
}

export interface PointPageEnvelope<T = any> {
  success?: boolean
  data?: T[]
  total?: number
  pagination?: PointPagination
}

export type PointPageQuery = Record<string, string | number>
export type PointPageFetcher<T = any> = (query: PointPageQuery) => Promise<PointPageEnvelope<T> | null | undefined>

export class PointCatalogueIncompleteError extends Error {
  readonly code = 'POINT_CATALOGUE_INCOMPLETE'
  constructor(detail: string) {
    super(`El catálogo de agencias no se pudo leer completo: ${detail}`)
    this.name = 'PointCatalogueIncompleteError'
  }
}

/**
 * The all-or-nothing walk shared by every bounded catalogue. `fail` builds the catalogue's own error and
 * `entity` names a row in its messages ("la agencia", "el grupo").
 */
export async function readAllCataloguePages<T extends { id?: unknown } = any>(
  fetchPage: PointPageFetcher<T>,
  params: PointPageQuery,
  fail: (detail: string) => Error,
  entity: string
): Promise<T[]> {
  const rows: T[] = []
  const seen = new Set<unknown>()
  for (let page = 1; page <= POINT_CATALOGUE_MAX_PAGES; page += 1) {
    const envelope = await fetchPage({ ...params, page, limit: POINT_CATALOGUE_PAGE_LIMIT })
    const data = envelope?.data
    const pagination = envelope?.pagination
    if (!Array.isArray(data) || !pagination || !Number.isInteger(pagination.total) || typeof pagination.hasMore !== 'boolean') {
      throw fail('la respuesta no trae el contrato de paginación')
    }
    for (const row of data) {
      if (seen.has(row?.id)) throw fail(`${entity} ${String(row?.id)} se repitió entre páginas`)
      seen.add(row?.id)
      rows.push(row)
    }
    if (!pagination.hasMore) {
      if (rows.length !== pagination.total) {
        throw fail(`se leyeron ${rows.length} de ${pagination.total}`)
      }
      return rows
    }
    if (data.length === 0) throw fail(`la página ${page} llegó vacía con más páginas pendientes`)
  }
  throw fail('se agotó el presupuesto de páginas')
}

export async function fetchAllPointPages<T extends { id?: unknown } = any>(
  fetchPage: PointPageFetcher<T>,
  params: PointPageQuery = {}
): Promise<T[]> {
  return readAllCataloguePages(fetchPage, params, (detail) => new PointCatalogueIncompleteError(detail), 'la agencia')
}

/** The page fetcher for a client built on the canonical shared instance: keeps the envelope. */
export function sharedClientPointPages(
  client: { get: (url: string, config?: any) => Promise<{ data: any }> },
  path: string
): PointPageFetcher {
  return async (query) => (await client.get(path, { params: query, skipUnwrap: true })).data
}
